/**
 * Entregas (E3): pendientes por entregar con antigüedad, entrega parcial/total desde un almacén, historial y formato
 * "Entrega de productos". Fase 1 de retiros (2026-10-29): «Dejar pendiente» sobre una entrega del mismo día con su
 * «Constancia de pendiente por retirar», el correo «Estado de su retiro» y «Copiar mensaje para WhatsApp».
 * Fase 4 (2026-10-29): retiro en otra sucursal: «Otra sucursal» crea la orden de retiro, «Para retirar aquí desde otra
 * sucursal» y la pestaña «Órdenes de retiro» (OrdenesRetiro.tsx). El despacho de un retiro en otra sucursal dice
 * «Factura N.º … de <venta>» y «retirada en <retiro>» en las dos líneas que ya tenía (Ref. y Almacén): no agrega líneas.
 */
import { FORMATO_DESPACHO_ETIQUETA, FORMATOS_DESPACHO, formatearCantidad, Money, MOTIVOS_DEJAR_PENDIENTE, type AlmacenDTO, type CorreoAutomaticoDTO, type EntregaDTO, type FormatoDespacho, type PendienteEntregaDTO, type SucursalDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightLeft, PackageCheck, PackageMinus, Printer, ShieldAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useTemaAyuda } from '@/ayuda/contexto.tsx';
import { useAutorizacion } from '@/componentes/Autorizacion.tsx';
import { copiarEstadoRetiro, CopiarWhatsApp } from '@/componentes/CopiarWhatsApp.tsx';
import { mostrarRegistrado } from '@/componentes/Registrado.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { Leyendas, Membrete, useEmpresaImpresion } from '@/componentes/Impresion.tsx';
import { LETRA_MATRIZ, MATRIZ_TRAZO_PX } from '@/modulos/ventas/FormatoFiscal.tsx';
import { FiltroFechas } from '@/componentes/FiltroFechas.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { useRegistroImpresion } from '@/lib/impresion.ts';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { generarAlertasRetiro, registrarImpresionEntrega, registrarImpresionReingreso, useDejarPendiente, useEntrega, useEntregas, usePendientes, useRegistrarEntrega, useReingreso } from './api.ts';
import { normalizarDecimal, parseMonto } from '@/lib/montos.ts';
import { DialogoOrdenRetiro, ListaOrdenesRetiro, ParaRetirarAqui } from './OrdenesRetiro.tsx';

const cant = (v: string) => formatearCantidad(Money.parse(v));
/** «de IVA y municipal»: qué retenciones de la factura siguen por recibir. */
const textoRetenciones = (r: PendienteEntregaDTO['retenciones_por_recibir']) => r.map((x) => (x.tipo === 'iva' ? 'de IVA' : 'municipal')).join(' y ');
type SucursalMia = SucursalDTO & { almacenes: AlmacenDTO[] };
type Pestana = 'pendientes' | 'historial' | 'ordenes';
const TEMA_PESTANA: Record<Pestana, string> = { pendientes: 'entregas.pendientes', historial: 'entregas.historial', ordenes: 'entregas.ordenes-retiro' };
const TITULO_PESTANA: Record<Pestana, string> = { pendientes: 'Por entregar', historial: 'Historial', ordenes: 'Órdenes de retiro' };
/** Lo que se entrega aquí de un renglón: lo pendiente menos lo apartado en otra sucursal con la orden de retiro. */
const aquiDe = (l: PendienteEntregaDTO['lineas'][number]) => Money.parse(l.pendiente).sub(Money.parse(l.en_orden_retiro ?? '0'));
/**
 * Filas que pide cada pestaña. Si hay más, la tabla lo avisa al pie con el total (re-auditoría 2026-10-18, ronda 2,
 * hallazgo 2-H16): Por entregar muestra las más antiguas y el Historial las más recientes.
 */
const TOPE_LISTA = 50;
const AVISO_TOPE = 'border-t bg-amber-50 px-4 py-2 text-xs text-amber-900';
const diaCaracas = (d: Date | string) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' }).format(new Date(d));
/** «Dejar pendiente» solo el mismo día de la entrega (hora de Caracas); el servidor lo vuelve a revisar. */
const esDeHoy = (iso: string) => diaCaracas(iso) === diaCaracas(new Date());
/** Qué pasó con el correo automático «Estado de su retiro», para la ventana que queda en pantalla. */
const renglonCorreo = (c: CorreoAutomaticoDTO | undefined) => (c && c.estado !== 'no_aplica' ? [{ texto: c.mensaje, aviso: c.estado !== 'enviado' }] : []);

export function Entregas() {
  const [tab, setTab] = useState<Pestana>('pendientes');
  useTemaAyuda(TEMA_PESTANA[tab]);
  const [q, setQ] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const pendientes = usePendientes({ q: q || undefined, desde: desde || undefined, hasta: hasta || undefined, limite: TOPE_LISTA });
  // Material sin retirar pasado el plazo: al abrir la pantalla se avisa (una vez por venta) a la administración y a la dueña
  const avisado = useRef(false);
  useEffect(() => {
    if (avisado.current) return;
    avisado.current = true;
    generarAlertasRetiro().then((r) => { if (r.generadas > 0) toast.warning(`${r.generadas} venta(s) con material sin retirar pasado el plazo: administración y dueña avisadas`); }).catch(() => undefined);
  }, []);
  const historial = useEntregas({ q: q || undefined, desde: desde || undefined, hasta: hasta || undefined, limite: TOPE_LISTA });
  const mia = useQuery({ queryKey: claves.sucursalMia, queryFn: () => get<SucursalMia | null>('/sucursales/mia') });
  const [entregando, setEntregando] = useState<PendienteEntregaDTO | null>(null);
  /** Entrega recién registrada: se muestra como en ventas, con el botón Imprimir (pedido del cliente 03-10-2026). */
  const [registrada, setRegistrada] = useState<EntregaDTO | null>(null);
  /** Entrega sobre la que se usa «Dejar pendiente» (Fase 1 de retiros). */
  const [dejandoId, setDejandoId] = useState<string | null>(null);
  /** Venta para la que se hace la orden de retiro en otra sucursal (Fase 4). */
  const [ordenDe, setOrdenDe] = useState<string | null>(null);
  const [almacenId, setAlmacenId] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  const [recibe, setRecibe] = useState('');
  // La cédula de quien retira y las observaciones van en la hoja de despacho (hallazgo D20 del 2026-10-18)
  const [recibeCedula, setRecibeCedula] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const registrar = useRegistrarEntrega();
  const aut = useAutorizacion();
  const { puede, usuario } = useAuth();
  const [enviando, setEnviando] = useState(false);
  // Quien registra entregas elige cualquier almacén de su sucursal, también el cajero: en el depósito no hay equipo
  // y el retiro lo anota ventas (alcance ENT-03; el servidor aplica la misma regla)
  const almacenes = mia.data?.almacenes ?? [];

  useEffect(() => {
    if (entregando) {
      setCantidades(Object.fromEntries(entregando.lineas.map((l) => [l.documento_linea_id, aquiDe(l).toDocumento()])));
      // Por defecto despacha el almacén donde se reservó (venta contra depósito); si hay varios, la tienda
      const reservas = [...new Set(entregando.lineas.map((l) => l.almacen_id).filter((x): x is string => !!x))];
      const unico = reservas.length === 1 ? almacenes.find((a) => a.id === reservas[0]) : undefined;
      setAlmacenId(unico?.id ?? almacenes.find((a) => a.principal)?.id ?? almacenes[0]?.id ?? '');
      setRecibe(entregando.cliente_razon_social);
      setRecibeCedula('');
      setObservaciones('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entregando?.documento_id]);

  const confirmar = async () => {
    if (!entregando) return;
    const lineas = Object.entries(cantidades).filter(([, v]) => parseMonto(v)?.esPositivo()).map(([documento_linea_id, cantidad]) => ({ documento_linea_id, cantidad: normalizarDecimal(cantidad) }));
    const datos = { almacen_id: almacenId, lineas, recibe_nombre: recibe || null, ...(recibeCedula.trim() ? { recibe_documento: recibeCedula.trim() } : {}), ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}) };
    const retenciones = entregando.retenciones_por_recibir;
    setEnviando(true);
    try {
      // Con la retención por recibir, quien no tiene el permiso pide la autorización de la dueña o de un administrador
      const e = await aut.ejecutar((ids) => registrar.mutateAsync({ documentoId: entregando.documento_id, datos: { ...datos, ...(ids.length > 0 ? { autorizaciones: ids } : {}) } }), {
        motivo: 'Entregar con la retención por recibir',
        detalle: { documento: `${entregando.tipo} ${entregando.numero_formateado}`, cliente: entregando.cliente_razon_social, retencion: retenciones.length > 0 ? textoRetenciones(retenciones) : null },
      });
      if (!e) return;
      setEntregando(null);
      setRegistrada(e);
      // La hoja de despacho se abre de una vez, como el documento al vender
      window.open(`/imprimir/ENT/${e.id}`, '_blank');
    } catch (err) {
      toast.error('No se pudo entregar', { description: mensajeDeError(err) });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <TituloPagina
        titulo="Entregas"
        subtitulo="Pendientes por entregar (antigüedad), entregas parciales o totales con formato imprimible."
        acciones={
          <>
            <FiltroFechas desde={desde} hasta={hasta} onChange={(d, h) => { setDesde(d); setHasta(h); }} testid="entregas" />
            <Input placeholder="Buscar documento o cliente…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="entregas-buscar" />
          </>
        }
      />
      <div className="mb-3 inline-flex overflow-hidden rounded-lg border">
        {(['pendientes', 'historial', 'ordenes'] as const).map((t) => (
          <button key={t} className={`px-4 py-1.5 text-sm font-medium ${tab === t ? 'bg-mg-verde text-white' : 'bg-card'}`} onClick={() => setTab(t)} data-testid={`entregas-tab-${t}`}>
            {t === 'pendientes' ? `Por entregar${pendientes.data ? ` (${pendientes.data.total})` : ''}` : TITULO_PESTANA[t]}
          </button>
        ))}
      </div>
      {tab === 'pendientes' ? <ParaRetirarAqui /> : null}
      {tab === 'ordenes' ? (
        <ListaOrdenesRetiro />
      ) : tab === 'pendientes' ? (
        <EstadoQuery cargando={pendientes.isLoading} error={pendientes.error} datos={pendientes.data} reintentar={() => void pendientes.refetch()}>
          {(d) =>
            d.datos.length === 0 ? (
              <Vacio icono={<PackageCheck className="h-6 w-6" />} titulo="Nada pendiente por entregar" descripcion="Todas las ventas de la sucursal están entregadas." />
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Documento</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Emitido</TableHead>
                    <TableHead className="text-right">Antigüedad</TableHead>
                    <TableHead className="text-right">Unid. pendientes</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Aviso al cliente</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.datos.map((p) => (
                    <TableRow key={p.documento_id} data-testid={`pendiente-${p.numero_formateado}`}>
                      <TableCell className="font-medium">{p.tipo} {p.numero_formateado}</TableCell>
                      <TableCell>{p.cliente_razon_social}</TableCell>
                      <TableCell>{formatearFechaHora(p.emitido_en)}</TableCell>
                      <TableCell className="text-right"><Badge variant={p.vencido ? 'critico' : p.dias > 2 ? 'pendiente' : 'neutro'}>{p.dias} d</Badge>{p.vencido ? <span className="block text-[11px] font-medium text-mg-rojo" data-testid={`pendiente-vencido-${p.numero_formateado}`}>{p.dias_habiles} días hábiles sin retirar</span> : <span className="block text-[11px] text-muted-foreground">{p.dias_habiles} días hábiles</span>}</TableCell>
                      <TableCell className="text-right num">
                        {cant(p.unidades_pendientes)}
                        {/* De dónde se retira (tienda o depósito) */}
                        <p className="text-[11px] font-normal text-muted-foreground" data-testid={`pendiente-almacen-${p.numero_formateado}`}>{[...new Set(p.lineas.map((l) => l.almacen_nombre).filter(Boolean))].join(' · ')}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant={p.estado_entrega === 'parcial' ? 'pendiente' : 'neutro'}>{p.estado_entrega === 'parcial' ? 'Parcial' : 'Sin entregar'}</Badge>
                        {p.retenciones_por_recibir.length > 0 ? <Badge variant="critico" className="ml-1" data-testid={`pendiente-retencion-${p.numero_formateado}`}>Retención por recibir</Badge> : null}
                        {/* El cliente no se llevó todo lo de una entrega («Dejar pendiente») */}
                        {p.dejado_pendiente ? <Badge variant="info" className="ml-1" data-testid={`pendiente-dejado-${p.numero_formateado}`}>Dejado pendiente</Badge> : null}
                        {/* Retiro en otra sucursal: lo indicado está apartado allá con su orden */}
                        {p.orden_retiro ? <Badge variant="info" className="ml-1" title={`Orden ${p.orden_retiro.numero_formateado} · vence ${formatearFechaHora(p.orden_retiro.vence_en)}`} data-testid={`pendiente-orden-${p.numero_formateado}`}>Retira en {p.orden_retiro.sucursal_retiro_nombre} · {p.orden_retiro.numero_formateado}</Badge> : null}
                      </TableCell>
                      {/* Si ya se le envió el correo «Estado de su retiro» (automático o a mano) y si salió */}
                      <TableCell className="text-xs" data-testid={`pendiente-correo-${p.numero_formateado}`}>
                        {p.correo_retiro ? (
                          p.correo_retiro.estado === 'enviado' ? <Badge variant="ok" title={formatearFechaHora(p.correo_retiro.enviado_en)}>Correo {formatearFechaHora(p.correo_retiro.enviado_en, { hour: undefined, minute: undefined })}</Badge> : <Badge variant="critico">Correo no salió</Badge>
                        ) : (
                          <span className="text-muted-foreground">Sin correo</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <CopiarWhatsApp documentoId={p.documento_id} enFila />
                          {!p.orden_retiro && p.lineas.some((l) => !l.sin_inventario) ? <Button size="sm" variant="outline" onClick={() => setOrdenDe(p.documento_id)} title="Retirar en otra sucursal (orden de retiro)" data-testid={`otra-sucursal-${p.numero_formateado}`}><ArrowRightLeft /> Otra sucursal</Button> : null}
                          <Button size="sm" onClick={() => setEntregando(p)} disabled={p.lineas.length > 0 && p.lineas.every((l) => !aquiDe(l).esPositivo())} data-testid={`entregar-${p.numero_formateado}`}>Entregar</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {d.total > d.datos.length ? <p className={AVISO_TOPE} data-testid="entregas-pendientes-tope">Se muestran las {d.datos.length} ventas más antiguas de {d.total} por entregar: usa el buscador o las fechas para ver el resto.</p> : null}
              </>
            )
          }
        </EstadoQuery>
      ) : (
        <EstadoQuery cargando={historial.isLoading} error={historial.error} datos={historial.data} reintentar={() => void historial.refetch()}>
          {(d) =>
            d.datos.length === 0 ? (
              <Vacio titulo="Sin entregas registradas" />
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entrega</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Almacén</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Por</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.datos.map((e) => (
                    <TableRow key={e.id} data-testid={`entrega-${e.numero_formateado}`}>
                      <TableCell className="font-medium">{e.numero_formateado}</TableCell>
                      <TableCell>{e.documento_tipo} {e.documento_numero}{e.retiro_otra_sucursal ? <p className="text-[11px] text-muted-foreground" data-testid={`entrega-otra-sucursal-${e.numero_formateado}`}>{e.retiro_otra_sucursal}</p> : null}</TableCell>
                      <TableCell>{e.cliente_razon_social}</TableCell>
                      <TableCell>{e.almacen_nombre}</TableCell>
                      <TableCell>{formatearFechaHora(e.entregado_en)}</TableCell>
                      <TableCell>{e.usuario_nombre}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {esDeHoy(e.entregado_en) && !e.retiro_otra_sucursal ? <Button size="sm" variant="outline" onClick={() => setDejandoId(e.id)} data-testid={`dejar-pendiente-${e.numero_formateado}`}><PackageMinus /> Dejar pendiente</Button> : null}
                          <Button size="sm" variant="outline" onClick={() => window.open(`/imprimir/ENT/${e.id}`, '_blank')}><Printer /> Imprimir</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {d.total > d.datos.length ? <p className={AVISO_TOPE} data-testid="entregas-historial-tope">Se muestran las {d.datos.length} entregas más recientes de {d.total}: usa el buscador o las fechas para ver el resto.</p> : null}
              </>
            )
          }
        </EstadoQuery>
      )}

      <Dialog open={!!entregando} onOpenChange={(o) => !o && setEntregando(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialogo-entrega">
          {entregando ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-1"><DialogTitle>Entregar {entregando.tipo} {entregando.numero_formateado} · {entregando.cliente_razon_social}</DialogTitle><AyudaVentana tema="entregas.entregar" /></div>
                <DialogDescription>Indica cuántas unidades salen ahora por línea (≤ pendiente). Lo que no entregues queda pendiente.</DialogDescription>
              </DialogHeader>
              {entregando.retenciones_por_recibir.length > 0 ? (
                <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900" role="alert" data-testid="entrega-aviso-retencion">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    La retención {textoRetenciones(entregando.retenciones_por_recibir)} de esta factura está por recibir.{' '}
                    {puede('inventario.entrega-sin-retencion')
                      ? `Tu usuario puede entregar igual, completa o en parte: queda anotado${usuario?.rol === 'superadmin' ? '' : ' y la dueña recibe el aviso'}.`
                      : 'Para entregar, completa o en parte, hace falta la autorización de la dueña o de un administrador.'}
                  </span>
                </p>
              ) : null}
              <div className="grid grid-cols-2 gap-3">
                <Campo etiqueta="Almacén que despacha" htmlFor="ent-almacen">
                  <Select id="ent-almacen" value={almacenId} onChange={(e) => setAlmacenId(e.target.value)} data-testid="entrega-almacen">
                    {almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}{a.principal ? ' (principal)' : ''}</option>)}
                  </Select>
                </Campo>
                <Campo etiqueta="Recibe" htmlFor="ent-recibe">
                  <Input id="ent-recibe" value={recibe} onChange={(e) => setRecibe(e.target.value)} />
                </Campo>
                <Campo etiqueta="Cédula de quien retira" htmlFor="ent-cedula">
                  <Input id="ent-cedula" value={recibeCedula} onChange={(e) => setRecibeCedula(e.target.value)} placeholder="V-12345678" data-testid="entrega-cedula" />
                </Campo>
                <Campo etiqueta="Observaciones" htmlFor="ent-obs">
                  <Input id="ent-obs" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Placa del vehículo, chofer…" data-testid="entrega-observaciones" />
                </Campo>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead className="text-right">Vendido</TableHead>
                    <TableHead className="text-right">Entregado</TableHead>
                    <TableHead className="text-right">Pendiente</TableHead>
                    <TableHead className="text-right">Entregar ahora</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entregando.lineas.map((l) => (
                    <TableRow key={l.documento_linea_id}>
                      <TableCell>
                        <p className="font-medium">{l.producto_nombre}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {l.producto_codigo}
                          {/* Dónde está apartada la mercancía: una venta contra depósito se retira en ese depósito */}
                          {l.sin_inventario ? ' · pieza de vidrio o producto con detalle: se entrega sin mover existencia' : l.almacen_nombre ? (
                            <span className={l.almacen_id !== almacenId ? 'font-medium text-amber-700' : undefined} data-testid={`entrega-reserva-${l.producto_codigo}`}> · apartado en {l.almacen_nombre}</span>
                          ) : null}
                          {Money.parse(l.en_orden_retiro ?? '0').esPositivo() && entregando.orden_retiro ? (
                            <span className="font-medium text-blue-700" data-testid={`entrega-en-orden-${l.producto_codigo}`}> · {cant(l.en_orden_retiro)} para retirar en {entregando.orden_retiro.sucursal_retiro_nombre} (orden {entregando.orden_retiro.numero_formateado}): aquí hasta {cant(aquiDe(l).toDocumento())}</span>
                          ) : null}
                        </p>
                      </TableCell>
                      <TableCell className="text-right num">{cant(l.unidades)}</TableCell>
                      <TableCell className="text-right num">{cant(l.entregado)}</TableCell>
                      <TableCell className="text-right num font-semibold">{cant(l.pendiente)}</TableCell>
                      <TableCell className="text-right"><Input inputMode="decimal" value={cantidades[l.documento_linea_id] ?? ''} onChange={(e) => setCantidades((c) => ({ ...c, [l.documento_linea_id]: e.target.value }))} className="h-8 w-24 text-right" aria-label={`Entregar ${l.producto_codigo}`} data-testid={`entrega-cantidad-${l.producto_codigo}`} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEntregando(null)}>Cancelar</Button>
                <Button onClick={() => void confirmar()} cargando={registrar.isPending || enviando} disabled={!almacenId || enviando} data-testid="confirmar-entrega"><PackageCheck /> Registrar entrega</Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {aut.dialogo}

      <Dialog open={!!registrada} onOpenChange={(o) => !o && setRegistrada(null)}>
        <DialogContent className="max-w-md" data-testid="dialogo-entrega-registrada">
          {registrada ? (
            <>
              <DialogHeader>
                <DialogTitle>Entrega {registrada.numero_formateado} registrada</DialogTitle>
                <DialogDescription>
                  {registrada.documento_tipo} {registrada.documento_numero} · {registrada.cliente_razon_social} · {registrada.almacen_nombre}
                </DialogDescription>
              </DialogHeader>
              <p className="text-sm">{registrada.documento_estado_entrega === 'entregado' ? 'El documento quedó entregado por completo.' : 'El documento queda con entrega parcial.'} La hoja de despacho se abrió en otra pestaña; si no la ves, usa Imprimir.</p>
              {renglonCorreo(registrada.correo_retiro).map((r) => (
                <p key={r.texto} className={r.aviso ? 'rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900' : 'text-sm text-muted-foreground'} data-testid="entrega-registrada-correo">{r.texto}</p>
              ))}
              {registrada.dejar_pendiente_disponible ? <p className="text-xs text-muted-foreground">Si el cliente al final no se lleva todo (no le cupo, se lleva una parte), usa <b>Dejar pendiente</b>: vuelve al almacén y queda apartado para él.</p> : null}
              <DialogFooter className="flex-wrap gap-2 sm:space-x-0">
                {registrada.dejar_pendiente_disponible ? <Button variant="outline" onClick={() => { setDejandoId(registrada.id); setRegistrada(null); }} data-testid="entrega-registrada-dejar"><PackageMinus /> Dejar pendiente</Button> : null}
                <CopiarWhatsApp documentoId={registrada.documento_id} />
                <Button variant="outline" onClick={() => window.open(`/imprimir/ENT/${registrada.id}`, '_blank')} data-testid="entrega-registrada-imprimir"><Printer /> Imprimir</Button>
                <Button onClick={() => setRegistrada(null)} data-testid="entrega-registrada-cerrar">Listo</Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {dejandoId ? <DialogoDejarPendiente entregaId={dejandoId} cerrar={() => setDejandoId(null)} /> : null}
      {ordenDe ? <DialogoOrdenRetiro documentoId={ordenDe} cerrar={() => setOrdenDe(null)} /> : null}
    </>
  );
}

/**
 * «Dejar pendiente» (Fase 1 de retiros, respuestas de la dueña del 29-10): el cliente al final no se lleva todo lo de
 * una entrega de HOY. Por renglón, cuánto queda (nunca más de lo que salió menos lo ya dejado) y el motivo. Lo indicado
 * vuelve al almacén que despachó y queda apartado para el cliente. No es una devolución: no hay nota de crédito ni caja.
 * Al registrar, la «Constancia de pendiente por retirar» se ofrece en la ventana que queda en pantalla.
 */
export function DialogoDejarPendiente({ entregaId, cerrar }: { entregaId: string; cerrar: () => void }) {
  const e = useEntrega(entregaId);
  const dejar = useDejarPendiente();
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState<string>(MOTIVOS_DEJAR_PENDIENTE[0]);
  const [otro, setOtro] = useState('');
  const lineas = (e.data?.lineas ?? []).map((l) => ({ ...l, maximo: Money.parse(l.cantidad).sub(Money.parse(l.dejado_pendiente)) })).filter((l) => l.maximo.esPositivo());
  const motivoFinal = motivo === 'Otro' ? otro.trim() : motivo;
  const confirmar = async () => {
    const datos = { motivo: motivoFinal, lineas: Object.entries(cantidades).filter(([, v]) => parseMonto(v)?.esPositivo()).map(([documento_linea_id, cantidad]) => ({ documento_linea_id, cantidad: normalizarDecimal(cantidad) })) };
    try {
      const r = await dejar.mutateAsync({ entregaId, datos });
      cerrar();
      mostrarRegistrado({
        titulo: `Constancia ${r.numero_formateado}: queda pendiente por retirar`,
        descripcion: `${r.documento_tipo} ${r.documento_numero} · ${r.cliente_razon_social} · volvió a ${r.almacen_nombre}`,
        detalle: [
          { texto: r.documento_estado_entrega === 'pendiente' ? 'La venta queda sin entregar: todo vuelve a estar apartado para el cliente.' : 'La venta queda con entrega parcial: lo dejado vuelve a estar apartado para el cliente.' },
          { texto: 'Imprime la constancia y que el cliente la firme.' },
          ...renglonCorreo(r.correo_retiro),
        ],
        imprimir: [{ etiqueta: 'Imprimir constancia', ruta: `/imprimir/PEN/${r.id}` }],
        acciones: [{ etiqueta: 'Copiar mensaje para WhatsApp', alHacer: () => void copiarEstadoRetiro(r.documento_id), testid: 'registrado-whatsapp' }],
      });
    } catch (err) {
      toast.error('No se pudo dejar pendiente', { description: mensajeDeError(err) });
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !dejar.isPending && cerrar()}>
      <DialogContent className="max-w-2xl" data-testid="dialogo-dejar-pendiente">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Dejar pendiente{e.data ? ` · entrega ${e.data.numero_formateado}` : ''}</DialogTitle><AyudaVentana tema="entregas.dejar-pendiente" /></div>
          <DialogDescription>Lo que el cliente al final no se lleva vuelve al almacén que despachó y queda apartado para él. Solo el mismo día de la entrega; no es una devolución.</DialogDescription>
        </DialogHeader>
        <EstadoQuery cargando={e.isLoading} error={e.error} datos={e.data} reintentar={() => void e.refetch()}>
          {(d) =>
            !d.dejar_pendiente_disponible ? (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="dejar-pendiente-no-disponible">
                {esDeHoy(d.entregado_en) ? 'Ya se dejó pendiente todo lo que salió en esta entrega.' : 'Esta entrega no es de hoy: «Dejar pendiente» solo se usa el mismo día. Si el cliente trae mercancía de otro día, regístralo como devolución.'}
              </p>
            ) : (
              <>
                <p className="text-sm">{d.documento_tipo} {d.documento_numero} · {d.cliente_razon_social} · {d.almacen_nombre}</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead className="text-right">Salió</TableHead>
                      <TableHead className="text-right">Ya dejado</TableHead>
                      <TableHead className="text-right">Queda pendiente</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineas.map((l) => (
                      <TableRow key={l.documento_linea_id}>
                        <TableCell><p className="font-medium">{l.producto_nombre}</p><p className="text-[11px] text-muted-foreground">{l.producto_codigo} · hasta {cant(l.maximo.toDocumento())} {l.unidad}</p></TableCell>
                        <TableCell className="text-right num">{cant(l.cantidad)}</TableCell>
                        <TableCell className="text-right num">{cant(l.dejado_pendiente)}</TableCell>
                        <TableCell className="text-right"><Input inputMode="decimal" value={cantidades[l.documento_linea_id] ?? ''} onChange={(ev) => setCantidades((c) => ({ ...c, [l.documento_linea_id]: ev.target.value }))} className="h-8 w-24 text-right" aria-label={`Queda pendiente ${l.producto_codigo}`} data-testid={`dejar-cantidad-${l.producto_codigo}`} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="grid grid-cols-2 gap-3">
                  <Campo etiqueta="Motivo" htmlFor="dp-motivo">
                    <Select id="dp-motivo" value={motivo} onChange={(ev) => setMotivo(ev.target.value)} data-testid="dejar-motivo">
                      {MOTIVOS_DEJAR_PENDIENTE.map((m) => <option key={m} value={m}>{m}</option>)}
                      <option value="Otro">Otro</option>
                    </Select>
                  </Campo>
                  {motivo === 'Otro' ? (
                    <Campo etiqueta="Escribe el motivo" htmlFor="dp-otro">
                      <Input id="dp-otro" value={otro} onChange={(ev) => setOtro(ev.target.value)} maxLength={300} data-testid="dejar-motivo-otro" />
                    </Campo>
                  ) : <span />}
                </div>
              </>
            )
          }
        </EstadoQuery>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar} disabled={dejar.isPending}>Cancelar</Button>
          <Button
            onClick={() => void confirmar()}
            cargando={dejar.isPending}
            disabled={!e.data?.dejar_pendiente_disponible || motivoFinal.length < 3 || !Object.values(cantidades).some((v) => parseMonto(v)?.esPositivo())}
            data-testid="confirmar-dejar-pendiente"
          >
            <PackageMinus /> Dejar pendiente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Formato «Entrega de productos» (despacho): entregado y pendiente por renglón, firmas y las leyendas de cambios,
 * cortes y retiro. Sale en media carta VERTICAL (5,5 × 8,5 in) o en tiquera de 80 mm, según la caja que el usuario
 * tiene abierta (bloque 11); desde la propia hoja se puede cambiar de formato antes de imprimir.
 * Cada apertura de la hoja se cuenta y queda en la auditoría; desde la segunda la hoja dice «REIMPRESIÓN» (no «COPIA»:
 * pedido de la clienta), como la hoja del cierre y el recibo de abono (re-auditoría 2026-10-18, ronda 2, hallazgo 2-H11).
 */
const registrarCopiaEntrega = (id: string) => registrarImpresionEntrega(id).then((r) => r.copia);
const enlace = { textDecoration: 'underline', background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit' } as const;

export function ImprimirEntrega() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const e = useEntrega(id ?? null);
  const empresa = useEmpresaImpresion();
  const copia = useRegistroImpresion(id, registrarCopiaEntrega);
  useEffect(() => {
    if (e.data && empresa.data && copia !== null) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [e.data, empresa.data, copia]);
  if (!e.data || !empresa.data || copia === null) return <p style={{ padding: 24 }}>Preparando entrega…</p>;
  const d = e.data;
  const emp = empresa.data;
  const pedido = params.get('formato');
  const formato: FormatoDespacho = pedido === 'ticket_80' || pedido === 'media_carta' ? pedido : emp.despacho_formato;
  const ticket = formato === 'ticket_80';
  // Barinas (papel de factura en hoja carta de pie, 2026-11-03): el despacho, como la factura y la nota, a todo el ancho
  // en la mitad de arriba de una hoja carta de pie (en media carta de pie salía chico, en el centro de la hoja carta)
  const enMitad = emp.sucursal?.formato_impresion === 'media_carta_vertical';
  // Los lados, los de la factura de la sucursal (2026-11-03: en Barinas, 4 y 20 mm), como la nota
  const lados = emp.sucursal?.plantilla_factura;
  const relleno = lados ? `7mm ${lados.derecho_mm}mm 7mm ${lados.izquierdo_mm}mm` : '7mm 9mm';
  // Letra (pedido del usuario 2026-11-03: «demasiado pequeñas, deben ser muchísimo más grandes»): en la mitad de una hoja
  // carta, la de la matricial como la factura y la nota (Lucida Console con la negrita dibujada) a 13 px, la de la nota de
  // Barinas (10 pt). A 15 px la media hoja se llenaba con un producto: la cabecera va en dos renglones y las firmas lado a
  // lado, para que quepan unos 10. En media carta de pie, como siempre
  const t = enMitad ? { base: 13, titulo: 15, codigo: 10, firmas: 12, leyendas: 10.5, cabecera: 12 } : { base: 9.5, titulo: 11, codigo: 8, firmas: 8.5, leyendas: 8.5, cabecera: 8.5 };
  const leyendas = [emp.leyendas.retiro, emp.leyendas.cambios, emp.leyendas.cortes];
  // Retiro en otra sucursal (Fase 4): la referencia dice de qué sucursal es la venta y la línea del almacén, dónde se
  // retiró. Mismas líneas que una entrega normal (no mueve el formato)
  const ret = d.retiro_otra_sucursal;
  const referencia = ret ? <span data-testid="formato-entrega-retiro-venta">{d.documento_tipo === 'FAC' ? 'Factura' : 'Nota'} N.º {d.documento_numero} de {ret.sucursal_venta_nombre}</span> : <>Ref. {d.documento_tipo} {d.documento_numero}</>;
  const retiradaEn = ret ? `retirada en ${ret.sucursal_retiro_nombre} · ${ret.almacen_retiro_nombre}` : null;
  const barra = (
    <div className="ayuda-impresion" style={{ fontFamily: 'system-ui, sans-serif', fontSize: 12, background: '#fef3c7', padding: '6px 8px', marginBottom: 8, lineHeight: 1.4, WebkitTextStroke: 0, letterSpacing: 'normal' }} data-testid="despacho-ayuda">
      {ticket ? 'Tiquera de 80 mm: elige la impresora de tickets y márgenes «Ninguno».' : enMitad ? 'Hoja carta de pie, el despacho en la mitad de arriba (como la factura y la nota): papel «Carta», márgenes «Predeterminados» y escala 100 %.' : 'Media carta vertical: papel «Media carta» (5,5 × 8,5 in), orientación vertical, márgenes «Ninguno».'}{' '}
      {FORMATOS_DESPACHO.filter((f) => f !== formato).map((f) => (
        <button key={f} type="button" onClick={() => setParams({ formato: f }, { replace: true })} style={enlace} data-testid={`despacho-formato-${f}`}>
          Cambiar a {FORMATO_DESPACHO_ETIQUETA[f].toLowerCase()}
        </button>
      ))}
      {' · '}
      <button type="button" onClick={() => window.print()} style={enlace} data-testid="despacho-imprimir">Imprimir</button>
    </div>
  );
  const firmas = (
    <div style={{ marginTop: ticket ? 18 : 26, display: 'grid', gap: ticket ? 16 : 22 }}>
      <span>Entrega: {d.usuario_nombre} {ticket ? '' : '______________________________'}</span>
      {ticket ? <span>Firma: ____________________</span> : null}
      <span>Recibe: {d.recibe_nombre ?? ''} {d.recibe_documento ? `(${d.recibe_documento})` : ''} {ticket ? '' : '______________________________'}</span>
      {ticket ? <span>Firma: ____________________</span> : null}
    </div>
  );
  if (ticket) {
    // Tiquera térmica de 80 mm (72 mm útiles): una columna, letra de ancho fijo, el producto arriba y las cantidades abajo
    return (
      <div className="formato ticket" data-testid="formato-entrega" data-formato="ticket_80" data-reimpresion={copia ? 'si' : 'no'} style={{ fontFamily: "'Courier New', Courier, monospace", fontSize: 11.5, lineHeight: 1.25, width: '72mm', margin: '0 auto', padding: '2mm 0', color: '#000' }}>
        <style>{`@page { size: 80mm auto; margin: 0 4mm } html, body { background: #fff; margin: 0 } .ticket .c { text-align: center } .ticket hr { border: 0; border-top: 1px dashed #000; margin: 4px 0 } .ticket .fila { display: flex; justify-content: space-between; gap: 6px } @media screen { body { background: #e5e7eb } .ticket { background: #fff; box-shadow: 0 0 8px rgba(0,0,0,.2); margin: 16px auto !important; padding: 4mm !important } } @media print { .ayuda-impresion { display: none } }`}</style>
        {barra}
        <div className="c">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginBottom: 2 }}>
            <img src="/logo-mg.png" alt="MG Materiales y Decoraciones" style={{ height: 44, width: 'auto' }} data-testid="formato-logo-nuevo" />
            <img src="/logo-mg-antiguo.png" alt="Materiales y Decoraciones M.G. C.A." style={{ height: 26, width: 'auto' }} data-testid="formato-logo-antiguo" />
          </div>
          <div><b>{emp.razon_social.toUpperCase()}</b></div>
          <div>RIF {emp.rif} · {d.sucursal_nombre}</div>
          <div style={{ marginTop: 4 }}><b>ENTREGA DE PRODUCTOS</b></div>
          {copia ? <div style={{ letterSpacing: 2 }} data-testid="formato-entrega-reimpresion"><b>REIMPRESIÓN</b></div> : null}
          <div>N° <b data-testid="formato-entrega-numero">{d.numero_formateado}</b></div>
          <div>{formatearFechaHora(d.entregado_en)}</div>
          <div>{referencia}</div>
        </div>
        <hr />
        <div>Cliente: {d.cliente_razon_social}</div>
        <div>{d.cliente_rif}</div>
        {retiradaEn ? <div data-testid="formato-entrega-retirada-en">{retiradaEn}</div> : <div>Almacén: {d.almacen_nombre}</div>}
        <hr />
        {d.lineas.map((l) => (
          <div key={l.producto_codigo} style={{ marginBottom: 4 }} data-testid="formato-entrega-renglon">
            <div>{l.producto_nombre}</div>
            <div className="fila">
              <span>Entrega <b style={{ fontSize: '1.15em' }}>{cant(l.cantidad)}</b> {l.unidad}</span>
              <span>Pend. {cant(l.pendiente_despues)}</span>
            </div>
            <div data-testid="formato-entrega-vendido">Vendido {cant(l.unidades_doc)} {l.unidad}</div>
          </div>
        ))}
        {d.otros_pendientes.length > 0 ? (
          <>
            <hr />
            <div><b>Queda por entregar de esta venta</b></div>
            {d.otros_pendientes.map((o) => (
              <div key={`p-${o.producto_codigo}`} className="fila" data-testid="formato-entrega-pendiente">
                <span>{o.producto_nombre} (vendido {cant(o.vendido)})</span>
                <span>Pend. {cant(o.pendiente)}</span>
              </div>
            ))}
          </>
        ) : null}
        <hr />
        {d.observaciones ? <div>Obs.: {d.observaciones}</div> : null}
        {firmas}
        <Leyendas textos={leyendas} tamano={10} />
        <div className="c" style={{ marginTop: 10 }}>· · ·</div>
      </div>
    );
  }
  // Media carta VERTICAL (5,5 × 8,5 in): observación del cliente 24-09-2026 (salía horizontal en hoja completa).
  // En la Epson LX-300 el papel «Media carta» debe existir en Windows (Propiedades del servidor de impresión → Formularios).
  return (
    <div className="formato" data-testid="formato-entrega" data-formato={enMitad ? 'mitad_carta' : 'media_carta_vertical'} data-reimpresion={copia ? 'si' : 'no'} style={{ fontFamily: enMitad ? LETRA_MATRIZ : 'Arial, Helvetica, sans-serif', fontSize: t.base, letterSpacing: enMitad ? '0.2pt' : undefined, WebkitTextStroke: enMitad ? `${MATRIZ_TRAZO_PX}px #000` : undefined, lineHeight: enMitad ? 1.2 : undefined, width: enMitad ? '8.5in' : '5.1in', margin: '0 auto', padding: enMitad ? relleno : 6, boxSizing: enMitad ? 'border-box' : undefined, position: 'relative', color: '#000' }}>
      <style>{`@page { size: ${enMitad ? 'letter; margin: 0 0 5.5in 0' : '5.5in 8.5in; margin: 5mm'} } html, body { background: #fff; margin: 0 } .formato table { width: 100%; border-collapse: collapse } .formato th, .formato td { padding: 2px 3px; vertical-align: top } .formato th { border-bottom: 1px solid #000; text-align: left; font-size: ${t.cabecera}px } .num { text-align: right; white-space: nowrap } @media screen { body { background: #e5e7eb } .formato { background: #fff; box-shadow: 0 0 8px rgba(0,0,0,.2); margin: 16px auto; min-height: 8in } } @media print { .ayuda-impresion { display: none } }`}</style>
      {barra}
      <Membrete empresa={emp} compacto />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 5 }}>
        <div style={{ fontWeight: 'bold', fontSize: t.titulo }}>ENTREGA DE PRODUCTOS · {d.sucursal_nombre}{copia ? <span data-testid="formato-entrega-reimpresion"> · REIMPRESIÓN</span> : null}</div>
        <div style={{ textAlign: 'right' }}>
          {/* En la mitad de una hoja carta, el número y la fecha en un renglón: la cabecera ocupa dos */}
          <div>N° <b data-testid="formato-entrega-numero">{d.numero_formateado}</b>{enMitad ? <> · {formatearFechaHora(d.entregado_en)}</> : null}</div>
          {enMitad ? null : <div>{formatearFechaHora(d.entregado_en)}</div>}
          <div>{referencia}</div>
        </div>
      </div>
      <div style={{ marginBottom: 5 }}>
        <b>Cliente:</b> {d.cliente_razon_social} · {d.cliente_rif}
        {enMitad ? ' · ' : <br />}
        {retiradaEn ? <span data-testid="formato-entrega-retirada-en">{retiradaEn}</span> : <><b>Almacén:</b> {d.almacen_nombre}</>}
      </div>
      <table>
        <thead><tr><th>Descripción</th><th className="num">Vendido</th><th className="num">Entrega</th><th className="num">Pend.</th></tr></thead>
        <tbody>
          {d.lineas.map((l) => (
            <tr key={l.producto_codigo} data-testid="formato-entrega-renglon">
              <td>{l.producto_nombre} <span style={{ fontSize: t.codigo }}>({l.producto_codigo} · {l.unidad})</span></td>
              <td className="num">{cant(l.unidades_doc)}</td>
              <td className="num"><b>{cant(l.cantidad)}</b></td>
              <td className="num">{cant(l.pendiente_despues)}</td>
            </tr>
          ))}
          {/* Lo que sigue por entregar de la misma venta y no va en esta entrega */}
          {d.otros_pendientes.map((o) => (
            <tr key={`p-${o.producto_codigo}`} data-testid="formato-entrega-pendiente">
              <td>{o.producto_nombre} <span style={{ fontSize: t.codigo }}>({o.producto_codigo} · {o.unidad})</span></td>
              <td className="num">{cant(o.vendido)}</td>
              <td className="num">—</td>
              <td className="num">{cant(o.pendiente)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.observaciones ? <p style={{ fontSize: t.firmas }}>Obs.: {d.observaciones}</p> : null}
      {enMitad ? (
        // Las dos firmas lado a lado, en un renglón
        <div style={{ fontSize: t.firmas, marginTop: 16, display: 'flex', justifyContent: 'space-between', gap: 16 }} data-testid="formato-entrega-firmas">
          <span>Entrega: {d.usuario_nombre} ____________________</span>
          <span>Recibe: {d.recibe_nombre ?? ''} {d.recibe_documento ? `(${d.recibe_documento})` : ''} ____________________</span>
        </div>
      ) : (
        <div style={{ fontSize: t.firmas }}>{firmas}</div>
      )}
      <Leyendas textos={leyendas} tamano={t.leyendas} />
    </div>
  );
}

/**
 * «Constancia de pendiente por retirar» (formato nuevo de la Fase 1 de retiros, 2026-10-29): lo que el cliente dejó
 * pendiente en una entrega de hoy y lo que le queda por retirar, con espacio para su firma. Usa el estilo de la hoja de
 * despacho: media carta vertical o tiquera de 80 mm según la caja, con el cambio de formato en la barra de ayuda. Cada
 * apertura se cuenta y queda en la auditoría; desde la segunda dice «REIMPRESIÓN».
 */
const registrarCopiaConstancia = (id: string) => registrarImpresionReingreso(id).then((r) => r.copia);

export function ImprimirConstanciaPendiente() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const r = useReingreso(id ?? null);
  const empresa = useEmpresaImpresion();
  const copia = useRegistroImpresion(id, registrarCopiaConstancia);
  useEffect(() => {
    if (r.data && empresa.data && copia !== null) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [r.data, empresa.data, copia]);
  if (!r.data || !empresa.data || copia === null) return <p style={{ padding: 24 }}>Preparando constancia…</p>;
  const d = r.data;
  const emp = empresa.data;
  const pedido = params.get('formato');
  const formato: FormatoDespacho = pedido === 'ticket_80' || pedido === 'media_carta' ? pedido : emp.despacho_formato;
  const ticket = formato === 'ticket_80';
  const barra = (
    <div className="ayuda-impresion" style={{ fontFamily: 'system-ui, sans-serif', fontSize: 12, background: '#fef3c7', padding: '6px 8px', marginBottom: 8, lineHeight: 1.4 }} data-testid="constancia-ayuda">
      {ticket ? 'Tiquera de 80 mm: elige la impresora de tickets y márgenes «Ninguno».' : 'Media carta vertical: papel «Media carta» (5,5 × 8,5 in), orientación vertical, márgenes «Ninguno».'}{' '}
      {FORMATOS_DESPACHO.filter((f) => f !== formato).map((f) => (
        <button key={f} type="button" onClick={() => setParams({ formato: f }, { replace: true })} style={enlace} data-testid={`constancia-formato-${f}`}>
          Cambiar a {FORMATO_DESPACHO_ETIQUETA[f].toLowerCase()}
        </button>
      ))}
      {' · '}
      <button type="button" onClick={() => window.print()} style={enlace}>Imprimir</button>
    </div>
  );
  const texto = `El cliente deja pendiente por retirar lo indicado. La mercancía queda apartada a su nombre${d.almacen_nombre ? ` en ${d.almacen_nombre}` : ''}.`;
  const firmas = (
    <div style={{ marginTop: ticket ? 18 : 26, display: 'grid', gap: ticket ? 16 : 22 }}>
      <span>Registra: {d.usuario_nombre} {ticket ? '' : '______________________________'}</span>
      {ticket ? <span>Firma: ____________________</span> : null}
      <span>Cliente (firma y C.I.): {ticket ? '' : '______________________________'}</span>
      {ticket ? <span>____________________</span> : null}
    </div>
  );
  if (ticket) {
    return (
      <div className="formato ticket" data-testid="formato-constancia" data-formato="ticket_80" data-reimpresion={copia ? 'si' : 'no'} style={{ fontFamily: "'Courier New', Courier, monospace", fontSize: 11.5, lineHeight: 1.25, width: '72mm', margin: '0 auto', padding: '2mm 0', color: '#000' }}>
        <style>{`@page { size: 80mm auto; margin: 0 4mm } html, body { background: #fff; margin: 0 } .ticket .c { text-align: center } .ticket hr { border: 0; border-top: 1px dashed #000; margin: 4px 0 } .ticket .fila { display: flex; justify-content: space-between; gap: 6px } @media screen { body { background: #e5e7eb } .ticket { background: #fff; box-shadow: 0 0 8px rgba(0,0,0,.2); margin: 16px auto !important; padding: 4mm !important } } @media print { .ayuda-impresion { display: none } }`}</style>
        {barra}
        <div className="c">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginBottom: 2 }}>
            <img src="/logo-mg.png" alt="MG Materiales y Decoraciones" style={{ height: 44, width: 'auto' }} />
            <img src="/logo-mg-antiguo.png" alt="Materiales y Decoraciones M.G. C.A." style={{ height: 26, width: 'auto' }} />
          </div>
          <div><b>{emp.razon_social.toUpperCase()}</b></div>
          <div>RIF {emp.rif} · {d.sucursal_nombre}</div>
          <div style={{ marginTop: 4 }}><b>CONSTANCIA DE PENDIENTE POR RETIRAR</b></div>
          {copia ? <div style={{ letterSpacing: 2 }} data-testid="formato-constancia-reimpresion"><b>REIMPRESIÓN</b></div> : null}
          <div>N° <b data-testid="formato-constancia-numero">{d.numero_formateado}</b></div>
          <div>{formatearFechaHora(d.registrado_en)}</div>
          <div>Ref. {d.documento_tipo} {d.documento_numero} · Entrega {d.entrega_numero}</div>
        </div>
        <hr />
        <div>Cliente: {d.cliente_razon_social}</div>
        <div>{d.cliente_rif}</div>
        <div>Motivo: {d.motivo}</div>
        <hr />
        {d.lineas.map((l) => (
          <div key={l.producto_codigo} style={{ marginBottom: 4 }} data-testid="formato-constancia-renglon">
            <div>{l.producto_nombre}</div>
            <div className="fila">
              <span>Queda <b style={{ fontSize: '1.15em' }}>{cant(l.cantidad)}</b> {l.unidad}</span>
              <span>Por retirar {cant(l.pendiente_despues)}</span>
            </div>
            <div>Vendido {cant(l.unidades_doc)} {l.unidad}</div>
          </div>
        ))}
        {d.otros_pendientes.length > 0 ? (
          <>
            <hr />
            <div><b>También por retirar de esta venta</b></div>
            {d.otros_pendientes.map((o) => (
              <div key={`p-${o.producto_codigo}`} className="fila" data-testid="formato-constancia-pendiente">
                <span>{o.producto_nombre} (vendido {cant(o.vendido)})</span>
                <span>{cant(o.pendiente)}</span>
              </div>
            ))}
          </>
        ) : null}
        <hr />
        <div>{texto}</div>
        {firmas}
        <Leyendas textos={[emp.leyendas.retiro]} tamano={10} />
        <div className="c" style={{ marginTop: 10 }}>· · ·</div>
      </div>
    );
  }
  return (
    <div className="formato" data-testid="formato-constancia" data-formato="media_carta_vertical" data-reimpresion={copia ? 'si' : 'no'} style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 9.5, width: '5.1in', margin: '0 auto', padding: 6, position: 'relative', color: '#000' }}>
      <style>{`@page { size: 5.5in 8.5in; margin: 5mm } html, body { background: #fff; margin: 0 } .formato table { width: 100%; border-collapse: collapse } .formato th, .formato td { padding: 2px 3px; vertical-align: top } .formato th { border-bottom: 1px solid #000; text-align: left; font-size: 8.5px } .formato .num { text-align: right; white-space: nowrap } @media screen { body { background: #e5e7eb } .formato { background: #fff; box-shadow: 0 0 8px rgba(0,0,0,.2); margin: 16px auto; min-height: 8in } } @media print { .ayuda-impresion { display: none } }`}</style>
      {barra}
      <Membrete empresa={emp} compacto />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 5 }}>
        <div style={{ fontWeight: 'bold', fontSize: 11 }}>CONSTANCIA DE PENDIENTE POR RETIRAR · {d.sucursal_nombre}{copia ? <span data-testid="formato-constancia-reimpresion"> · REIMPRESIÓN</span> : null}</div>
        <div style={{ textAlign: 'right' }}>
          <div>N° <b data-testid="formato-constancia-numero">{d.numero_formateado}</b></div>
          <div>{formatearFechaHora(d.registrado_en)}</div>
          <div>Ref. {d.documento_tipo} {d.documento_numero}</div>
          <div>Entrega {d.entrega_numero}</div>
        </div>
      </div>
      <div style={{ marginBottom: 5 }}>
        <b>Cliente:</b> {d.cliente_razon_social} · {d.cliente_rif}
        <br />
        <b>Motivo:</b> {d.motivo}
      </div>
      <table>
        <thead><tr><th>Descripción</th><th className="num">Vendido</th><th className="num">Queda pendiente</th><th className="num">Por retirar</th></tr></thead>
        <tbody>
          {d.lineas.map((l) => (
            <tr key={l.producto_codigo} data-testid="formato-constancia-renglon">
              <td>{l.producto_nombre} <span style={{ fontSize: 8 }}>({l.producto_codigo} · {l.unidad})</span></td>
              <td className="num">{cant(l.unidades_doc)}</td>
              <td className="num"><b>{cant(l.cantidad)}</b></td>
              <td className="num">{cant(l.pendiente_despues)}</td>
            </tr>
          ))}
          {d.otros_pendientes.map((o) => (
            <tr key={`p-${o.producto_codigo}`} data-testid="formato-constancia-pendiente">
              <td>{o.producto_nombre} <span style={{ fontSize: 8 }}>({o.producto_codigo} · {o.unidad})</span></td>
              <td className="num">{cant(o.vendido)}</td>
              <td className="num">—</td>
              <td className="num">{cant(o.pendiente)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: 8.5 }}>{texto}</p>
      <div style={{ fontSize: 8.5 }}>{firmas}</div>
      <Leyendas textos={[emp.leyendas.retiro]} tamano={8.5} />
    </div>
  );
}
